import { Injectable } from '@angular/core';

export type GeoCountry = {
    countryCode: string | null;
    countryName: string | null;
};

export type GeoIpDetails = {
    ip: string | null;
    countryCode: string | null;
    countryName: string | null;
    region: string | null;
    city: string | null;
    latitude: number | null;
    longitude: number | null;
    timezone: string | null;
};

type GeoIpCache = {
    v: number;
    exp: number;
    data: GeoIpDetails;
};

const CACHE_KEY = 'nibni_geo_ip_v1';
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

function normalizeCountryCode(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return null;
    if (trimmed.length === 2) return trimmed;
    return null;
}

function normalizeText(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function normalizeNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

@Injectable({ providedIn: 'root' })
export class GeoService {
    peekGeoFromIpCache(): GeoIpDetails | null {
        if (typeof window === 'undefined') return null;
        return this.readCache();
    }

    async getGeoFromIp(): Promise<GeoIpDetails> {
        if (typeof window === 'undefined') {
            return {
                ip: null,
                countryCode: null,
                countryName: null,
                region: null,
                city: null,
                latitude: null,
                longitude: null,
                timezone: null
            };
        }

        const cached = this.readCache();
        if (cached) return cached;

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 2500);

        try {
            const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
            if (!response.ok) {
                return {
                    ip: null,
                    countryCode: null,
                    countryName: null,
                    region: null,
                    city: null,
                    latitude: null,
                    longitude: null,
                    timezone: null
                };
            }
            const data = await response.json();
            const result: GeoIpDetails = {
                ip: normalizeText(data?.ip),
                countryCode: normalizeCountryCode(data?.country),
                countryName: normalizeText(data?.country_name),
                region: normalizeText(data?.region),
                city: normalizeText(data?.city),
                latitude: normalizeNumber(data?.latitude),
                longitude: normalizeNumber(data?.longitude),
                timezone: normalizeText(data?.timezone)
            };
            this.writeCache(result);
            return result;
        } catch {
            return {
                ip: null,
                countryCode: null,
                countryName: null,
                region: null,
                city: null,
                latitude: null,
                longitude: null,
                timezone: null
            };
        } finally {
            window.clearTimeout(timeout);
        }
    }

    async getCountryFromIp(): Promise<GeoCountry> {
        const geo = await this.getGeoFromIp();
        return { countryCode: geo.countryCode, countryName: geo.countryName };
    }

    private readCache(): GeoIpDetails | null {
        try {
            const raw = localStorage.getItem(CACHE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw) as Partial<GeoIpCache>;
            if (parsed.v !== CACHE_VERSION) return null;
            if (!parsed.exp || typeof parsed.exp !== 'number') return null;
            if (Date.now() > parsed.exp) return null;
            const data = parsed.data as GeoIpDetails | undefined;
            if (!data) return null;
            return {
                ip: normalizeText(data.ip),
                countryCode: normalizeCountryCode(data.countryCode),
                countryName: normalizeText(data.countryName),
                region: normalizeText(data.region),
                city: normalizeText(data.city),
                latitude: normalizeNumber(data.latitude),
                longitude: normalizeNumber(data.longitude),
                timezone: normalizeText(data.timezone)
            };
        } catch {
            return null;
        }
    }

    private writeCache(data: GeoIpDetails): void {
        try {
            const payload: GeoIpCache = {
                v: CACHE_VERSION,
                exp: Date.now() + CACHE_TTL_MS,
                data
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
        } catch {
            // ignore storage failures
        }
    }
}
